import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { task, timeout } from 'ember-concurrency';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import {
  HdsPageHeader,
  HdsAlert,
  HdsTextDisplay,
  HdsTextBody,
  HdsLinkStandalone,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  PREFIX_VAULT_RADAR,
  VAULT_RADAR_ADMIN,
  VAULT_RADAR_USERS,
} from 'authz/utils/permission-types/index';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  MediaCard,
  Flex,
} from 'core/components';
import {
  isVaultRadarPaid,
  isVaultRadarDeleted,
  isVaultRadarTrial,
  TENANT_STATE,
  TENANT_TYPE,
  VAULT_RADAR_TENANT_STATES,
} from 'hcp/utils/vault-radar';

const VAULT_RADAR_ADMIN_PERMISSION = `${PREFIX_VAULT_RADAR}.${VAULT_RADAR_USERS}.${VAULT_RADAR_ADMIN}`;

const DELAY_FOR_VAULT_RADAR_TRANSITION = 2000;

export default class PageVaultRadar extends Component {
  @service intl;
  @service analytics;
  @service userContext;
  @service config;
  @service api;
  @service permissions;
  @service('config') appConfig;
  @service flashMessages;

  @tracked tenantData;
  @tracked hasExistingTenant;
  @tracked canProvisionTenant;

  trackWaitlistClick = () => {
    this.analytics.trackEvent('vault_radar_beta_waitlist_link_click');
  };

  setProvisionedTenant = task(async () => {
    const { project, organization } = this.userContext;

    try {
      await this.api.vaultRadar.tenant.provisionTenant(project.id, {
        location: {
          organizationId: organization.id,
        },
      });

      this.flashMessages.success(
        this.intl.t('components.page.vault-radar.toast.setup.success')
      );

      // Buy some time to show the success message
      await timeout(DELAY_FOR_VAULT_RADAR_TRANSITION);

      window.location.href = this.vaultRadarLink;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      this.flashMessages.error(
        this.intl.t('components.page.vault-radar.toast.setup.failed')
      );
    }
  });

  get vaultRadarLink() {
    const { project } = this.userContext;
    return `${this.appConfig.app.vaultRadarDomain}/projects/${project.id}`;
  }

  get tenantState() {
    // We still allow vault-radar users to see their data during delinquency, just not
    // start new trials
    const VIEWABLE_TENANT_STATES = [
      TENANT_STATE.PROVISIONED,
      TENANT_STATE.DELINQUENT,
      TENANT_STATE.DELAYED_DEPROVISIONING,
    ];

    const { tenantData, hasExistingTenant, canProvisionTenant } = this.args;
    const { state, type } = tenantData || {};
    const canProvision = !hasExistingTenant || type === TENANT_TYPE.INTERNAL;

    const isTenantClaimed =
      !canProvision && state === TENANT_STATE.NEW && this.isAdmin;
    const isActiveBeta =
      type === TENANT_TYPE.BETA && VIEWABLE_TENANT_STATES.includes(state);
    const isActivePaid = VIEWABLE_TENANT_STATES.includes(state);
    const isNewPaidTenant =
      isVaultRadarPaid(type) &&
      state === TENANT_STATE.NEW &&
      this.isAdmin &&
      canProvisionTenant;
    const isNewBeta =
      state === TENANT_STATE.NEW &&
      type === TENANT_TYPE.BETA &&
      this.isAdmin &&
      canProvisionTenant;
    const isDeletedTenant =
      isVaultRadarPaid(type) && isVaultRadarDeleted(state);
    const isTrialEnded = isVaultRadarTrial(type) && isVaultRadarDeleted(state);

    // For now, support Beta separately from Trial, where Customer and Standard have the
    // same behavior. After launch, beta and customer states will be deprecated.
    const isActiveTrial =
      isVaultRadarTrial(type) && state === TENANT_STATE.PROVISIONED;
    const isNewTrial =
      isVaultRadarTrial(type) &&
      state === TENANT_STATE.NEW &&
      canProvisionTenant;
    const hasBillingIssue =
      !canProvisionTenant && state === TENANT_STATE.NEW && this.isAdmin;

    switch (true) {
      case isDeletedTenant:
        return VAULT_RADAR_TENANT_STATES.DELETED_TENANT;
      case isTrialEnded:
        return VAULT_RADAR_TENANT_STATES.TRIAL_ENDED;
      case isTenantClaimed:
        return VAULT_RADAR_TENANT_STATES.CLAIMED_TENANT;
      case isActiveBeta:
        return VAULT_RADAR_TENANT_STATES.ACTIVE_BETA;
      case isActiveTrial:
        return VAULT_RADAR_TENANT_STATES.ACTIVE_TRIAL;
      case isActivePaid:
        return VAULT_RADAR_TENANT_STATES.ACTIVE_PAID;
      case hasBillingIssue:
        return VAULT_RADAR_TENANT_STATES.DISABLED;
      case isNewPaidTenant:
        return VAULT_RADAR_TENANT_STATES.NEW_PAID;
      case isNewBeta:
        return VAULT_RADAR_TENANT_STATES.NEW_BETA;
      case isNewTrial:
        return VAULT_RADAR_TENANT_STATES.NEW_TRIAL;
      default:
        return VAULT_RADAR_TENANT_STATES.SETUP_REQUIRED;
    }
  }

  get isAdmin() {
    return this.permissions.has(VAULT_RADAR_ADMIN_PERMISSION);
  }

  get isActive() {
    const activeStates = [
      VAULT_RADAR_TENANT_STATES.ACTIVE_PAID,
      VAULT_RADAR_TENANT_STATES.ACTIVE_BETA,
      VAULT_RADAR_TENANT_STATES.ACTIVE_TRIAL,
    ];
    return activeStates.includes(this.tenantState);
  }

  get isNew() {
    const newStates = [
      VAULT_RADAR_TENANT_STATES.NEW_PAID,
      VAULT_RADAR_TENANT_STATES.NEW_BETA,
      VAULT_RADAR_TENANT_STATES.NEW_TRIAL,
    ];
    return newStates.includes(this.tenantState);
  }

  get allowProvisioning() {
    const { canProvisionTenant } = this.args;
    return this.isNew && this.isAdmin && canProvisionTenant;
  }

  get ctaButtonText() {
    return this.tenantState === VAULT_RADAR_TENANT_STATES.NEW_TRIAL
      ? this.intl.t('components.page.vault-radar.cta-trial')
      : this.intl.t('components.page.vault-radar.cta');
  }

  get billingLink() {
    const { organization } = this.userContext;
    return `/billing/accounts/default-account/summary/index?org_id=${organization.id}`;
  }

  get alertSettings() {
    switch (this.tenantState) {
      case VAULT_RADAR_TENANT_STATES.DISABLED:
        return {
          title: this.intl.t(
            'components.page.vault-radar.alert.disabled.title'
          ),
          description: this.intl.t(
            'components.page.vault-radar.alert.disabled.description'
          ),
          color: 'critical',
          icon: 'alert-diamond',
        };
      case VAULT_RADAR_TENANT_STATES.CLAIMED_TENANT:
        return {
          title: this.intl.t('components.page.vault-radar.alert.claimed.title'),
          description: this.intl.t(
            'components.page.vault-radar.alert.claimed.description'
          ),
          color: 'warning',
          icon: 'alert-triangle',
        };
      case VAULT_RADAR_TENANT_STATES.ACTIVE_BETA:
        return {
          title: this.intl.t(
            'components.page.vault-radar.alert.beta-active.title'
          ),
          color: 'highlight',
          icon: 'info',
        };
      case VAULT_RADAR_TENANT_STATES.NEW_BETA:
        return {
          title: this.intl.t('components.page.vault-radar.alert.beta.title'),
          description: this.intl.t(
            'components.page.vault-radar.alert.beta.description'
          ),
          color: 'highlight',
          icon: 'info',
        };
      case VAULT_RADAR_TENANT_STATES.SETUP_REQUIRED:
        return {
          title: this.intl.t(
            'components.page.vault-radar.alert.setup-required.title'
          ),
          description: this.intl.t(
            'components.page.vault-radar.alert.setup-required.description'
          ),
          color: 'warning',
          icon: 'alert-triangle',
        };
      case VAULT_RADAR_TENANT_STATES.DELETED_TENANT:
        return {
          title: this.intl.t(
            'components.page.vault-radar.alert.deleted-tenant.title'
          ),
          description: this.intl.t(
            'components.page.vault-radar.alert.deleted-tenant.description'
          ),
          color: 'critical',
          icon: 'alert-diamond',
        };
      case VAULT_RADAR_TENANT_STATES.TRIAL_ENDED:
        return {
          title: this.intl.t(
            'components.page.vault-radar.alert.trial-ended.title'
          ),
          description: this.intl.t(
            'components.page.vault-radar.alert.trial-ended.description'
          ),
          color: 'highlight',
          icon: 'info',
        };
      default:
        return null;
    }
  }

  /**
   * @description Returns the qualified qualtrics link to our waitlist form
   * @returns {string}
   */
  get waitlistLink() {
    return this.intl.t(
      'components.page.vault-radar.overview.card.waitlist.href'
    );
  }
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @logo="vault-radar" />
          <PH.Title>
            {{t "components.page.vault-radar.title"}}
          </PH.Title>
        </HdsPageHeader>
      </:header>
      <:body>
        {{#if this.alertSettings}}
          <HdsAlert
            @type="inline"
            @color={{this.alertSettings.color}}
            @icon={{this.alertSettings.icon}}
            ...attributes
            data-test-vault-radar-banner
            as |A|
          >
            <A.Title>{{this.alertSettings.title}}</A.Title>
            {{#if this.alertSettings.description}}
              <A.Description>
                {{this.alertSettings.description}}
              </A.Description>
            {{/if}}
            {{#if (eq this.tenantState "ACTIVE_BETA")}}
              <A.Button
                @href={{this.vaultRadarLink}}
                @isHrefExternal={{false}}
                @text={{t "components.page.vault-radar.view-link-text"}}
                @color="secondary"
                data-test-vault-radar-banner-view-link
              />
            {{else if (eq this.tenantState "DISABLED")}}
              <A.Button
                @href={{this.billingLink}}
                @isHrefExternal={{false}}
                @text={{t
                  "components.page.vault-radar.alert.disabled.account-link-text"
                }}
                @color="secondary"
                data-test-vault-radar-banner-view-account-cta
              />
              <A.LinkStandalone
                @color="secondary"
                @icon="external-link"
                @iconPosition="trailing"
                @text={{t
                  "components.page.vault-radar.alert.disabled.support-link-text"
                }}
                @href="https://support.hashicorp.com/hc/en-us"
                data-test-vault-radar-banner-view-support-cta
              />
            {{else if this.allowProvisioning}}
              <A.Button
                {{on "click" this.setProvisionedTenant.perform}}
                disabled={{this.setProvisionedTenant.isRunning}}
                @text={{t "components.page.vault-radar.cta"}}
                @color="secondary"
                data-test-vault-radar-banner-cta
              />
            {{/if}}
          </HdsAlert>
        {{/if}}
        <MediaCard
          @variant="image"
          @src="/svgs/vault-radar-intro.svg"
          @mediaTitle={{t
            "components.page.vault-radar.overview.card.media.title"
          }}
          @alwaysStacked={{true}}
          @padding="0"
          data-test-media-card-vault-radar-card
        >
          <:content>
            <Flex
              @direction="column"
              @alignItems="center"
              @gap="md"
              class="vault-radar-overview__content"
            >
              <HdsTextDisplay
                @size="400"
                @weight="semibold"
                @tag="h1"
                @align="center"
              >
                {{t "components.page.vault-radar.overview.card.title"}}
              </HdsTextDisplay>
              <HdsTextBody
                @align="center"
                @tag="p"
                @color="var(--token-color-foreground-faint)"
              >
                {{t "components.page.vault-radar.overview.card.description"}}
              </HdsTextBody>
              {{#if this.allowProvisioning}}
                <HdsAlert
                  @type="compact"
                  @color="warning"
                  @icon="alert-triangle"
                  class="vault-radar-overview__content-disclaimer"
                  data-test-vault-radar-public-beta-limit-warning
                  as |A|
                >
                  <A.Description class="hds-font-weight-semibold">
                    {{t "components.page.vault-radar.overview.card.disclaimer"}}
                  </A.Description>
                </HdsAlert>
              {{/if}}
              <Flex
                @direction="column"
                @alignItems="center"
                @gap="md"
                class="vault-radar-overview__content-links"
              >
                {{#if this.isAdmin}}
                  <Flex @direction="row" @alignItems="center" @gap="2xs">
                    {{t
                      "components.page.vault-radar.overview.card.links.enterprise-customer.preface"
                    }}
                    <HdsLinkStandalone
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isRouteExternal={{true}}
                      @href={{t
                        "components.page.vault-radar.overview.card.links.enterprise-customer.href"
                      }}
                      @text={{t
                        "components.page.vault-radar.overview.card.links.enterprise-customer.title"
                      }}
                      data-test-vault-radar-enterprise-customer-link
                    />
                  </Flex>
                {{/if}}
                <HdsLinkStandalone
                  @icon="play-circle"
                  @iconPosition="trailing"
                  @isRouteExternal={{true}}
                  @href={{t
                    "components.page.vault-radar.overview.card.links.get-started.href"
                  }}
                  @text={{t
                    "components.page.vault-radar.overview.card.links.get-started.title"
                  }}
                  data-test-vault-radar-get-started-link
                />
                <HdsLinkStandalone
                  @icon="arrow-right"
                  @iconPosition="trailing"
                  @isRouteExternal={{true}}
                  @href={{t
                    "components.page.vault-radar.overview.card.links.learn-more.href"
                  }}
                  @text={{t
                    "components.page.vault-radar.overview.card.links.learn-more.title"
                  }}
                  data-test-vault-radar-learn-more-link
                />
              </Flex>
              {{#if this.isActive}}
                <HdsButton
                  @href={{this.vaultRadarLink}}
                  @isHrefExternal={{false}}
                  @text={{t "components.page.vault-radar.view-link-text"}}
                  data-test-vault-radar-view-vault-radar
                />
              {{else if this.allowProvisioning}}
                <HdsButton
                  {{on "click" this.setProvisionedTenant.perform}}
                  disabled={{this.setProvisionedTenant.isRunning}}
                  @text={{this.ctaButtonText}}
                  data-test-button-vault-radar-public-beta-cta
                />
              {{/if}}
            </Flex>
          </:content>
        </MediaCard>
      </:body>
    </CoreLayoutMain>
  </template>
}
